import { useState } from 'react';
import { X, Mail, Loader2, CheckCircle } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  darkMode: boolean;
}

export function AuthModal({ isOpen, onClose, darkMode }: AuthModalProps) {
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { signIn, verifyOtp } = useAuth();

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    const { error } = await signIn(email);
    
    if (error) {
      if (error.message.includes('rate limit')) {
        setError('Please wait a moment before trying again.');
      } else {
        setError(error.message);
      }
    } else {
      setSent(true);
    }
    setLoading(false);
  };

  const handleOtpSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    const { error } = await verifyOtp(email, otp);
    if (error) {
        setError(error.message);
    } else {
        onClose(); // Success!
    }
    setLoading(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className={`relative w-full max-w-sm rounded-3xl p-6 shadow-2xl ${darkMode ? 'bg-slate-800 text-white' : 'bg-white text-slate-900'}`}>
        <button 
          onClick={onClose}
          className={`absolute top-4 right-4 p-1 rounded-full ${darkMode ? 'hover:bg-slate-700' : 'hover:bg-slate-100'}`}
        >
          <X className="w-5 h-5" />
        </button>

        <div className="text-center mb-6">
          <h3 className="text-xl font-bold mb-2">Sign In / Sign Up</h3>
          <p className={`text-sm ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
            Sync your progress and badges across devices.
          </p>
        </div>

        {sent ? (
          <div className="text-center py-4">
            <div className="w-16 h-16 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8" />
            </div>
            <h4 className="font-bold text-lg mb-2">Check your email!</h4>
            <p className="text-sm text-slate-500 mb-6">
              Code sent to <span className="font-bold">{email}</span>.
              <br/>
              <span className="text-amber-600 font-bold text-xs block mt-2">
                ⚠️ iOS Users: Copy the code, do NOT click the link.
              </span>
            </p>

            {/* OTP Input */}
            <form onSubmit={handleOtpSubmit} className="space-y-4">
                <input 
                  type="text" 
                  placeholder="Enter Code"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value)}
                  className={`w-full text-center tracking-[0.2em] font-mono text-xl py-3 rounded-xl border-2 focus:ring-2 focus:ring-emerald-500/50 outline-none transition-all ${
                    darkMode 
                      ? 'bg-slate-900 border-slate-700 focus:border-emerald-500 text-white placeholder-slate-600' 
                      : 'bg-slate-50 border-slate-200 focus:border-emerald-500 text-slate-900 placeholder-slate-400'
                  }`}
                  maxLength={12}
                />
                
                {error && (
                  <div className="text-xs text-red-500 font-medium bg-red-50 p-2 rounded-lg border border-red-100">
                    {error}
                  </div>
                )}

                <button 
                  type="submit"
                  disabled={loading || otp.length < 4}
                  className="w-full py-3 bg-emerald-500 hover:bg-emerald-600 text-white font-bold rounded-xl shadow-lg shadow-emerald-500/20 transition-all flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
                >
                   {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Verify Code'}
                </button>
            </form>

            <button 
              onClick={onClose}
              className="mt-4 w-full py-2 text-sm text-slate-500 hover:text-slate-700 font-medium transition-colors"
            >
              Close
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider mb-1.5 ml-1">Email Address</label>
              <div className="relative">
                <Mail className={`absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 ${darkMode ? 'text-slate-500' : 'text-slate-400'}`} />
                <input 
                  type="email" 
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={`w-full pl-10 pr-4 py-3 rounded-xl border-2 focus:ring-2 focus:ring-emerald-500/20 outline-none transition-all ${
                    darkMode 
                      ? 'bg-slate-900 border-slate-700 focus:border-emerald-500 text-white placeholder-slate-600' 
                      : 'bg-slate-50 border-slate-200 focus:border-emerald-500 text-slate-900 placeholder-slate-400'
                  }`}
                  placeholder="you@example.com"
                />
              </div>
            </div>

            {error && (
              <div className="text-xs text-red-500 font-medium bg-red-50 p-2 rounded-lg border border-red-100">
                {error}
              </div>
            )}

            <button 
              type="submit" 
              disabled={loading}
              className="w-full py-3 bg-emerald-500 hover:bg-emerald-600 text-white font-bold rounded-xl shadow-lg shadow-emerald-500/20 transition-all flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
            >
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Send Login Code'}
            </button>
            
            <p className="text-[10px] text-center text-slate-400 mt-4">
              We will send a 6-digit code to your email.
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
